import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle, BarChart3, ChevronDown, ChevronRight, ExternalLink, Loader2,
  RefreshCw, Sparkles, TrendingDown, TrendingUp, Lightbulb, Leaf,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import {
  CONFIDENCE, HEAT_DAYPARTS, HEAT_WEEKDAYS, METRIC_KEYS, METRIC_LONG, METRIC_SHORT,
  RANGES, SORTS, coverageLine, engagementOf, filterArticles, formatCount, formatIndex,
  formatPct, groupArticles, heatIndex, indexBarWidth, sortArticles, tierForScore, tierMeta,
} from "@/lib/analyticsFormat";

/**
 * Analytics — which kinds of stories work for this property.
 *
 * "Working" (editor's definition) = website views + Facebook views +
 * social engagement. Every story is compared with the property's own
 * typical story over the chosen range (1.0× = typical); see
 * `backend/analytics_engine.py`. Numbers are collected nightly by the
 * 03:00 stats job, or on demand with "Sync now".
 */

const BTN_SHAPE = "inline-flex items-center gap-1.5 h-8 px-3 text-sm border disabled:opacity-50";
const BTN_IDLE =
  "border-[var(--border)] bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--secondary)]";
const BTN = `${BTN_SHAPE} ${BTN_IDLE}`;
const BTN_ACTIVE = "bg-[var(--slate-900)] text-white border-[var(--slate-900)]";
// A segmented button gets EITHER the idle colours OR the active ones,
// never both: with both on the element, Tailwind's stylesheet order let
// `bg-[var(--surface)]` beat `bg-[var(--slate-900)]`, so the selected
// range/sort button was white text on white in light mode (2026-09-28).
const segBtn = (active) => `${BTN_SHAPE} -ml-px first:ml-0 ${active ? BTN_ACTIVE : BTN_IDLE}`;
const CARD = "border border-[var(--border)] bg-[var(--surface)]";

function shortDate(ymd) {
  if (!ymd) return "";
  const d = new Date(`${ymd}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" }).format(d);
}

function ScoreBadge({ score, tier }) {
  const t = tier || tierForScore(score);
  const meta = tierMeta(t);
  return (
    <span
      className={`an-badge ${meta.cls} inline-flex items-center px-1.5 py-0.5 text-xs font-mono-display whitespace-nowrap`}
      title={meta.label}
    >
      {score === null || score === undefined ? meta.label : formatIndex(score)}
    </span>
  );
}

function ConfidenceTag({ level }) {
  const c = CONFIDENCE[level] || CONFIDENCE.thin;
  return (
    <span
      title={c.title}
      className={`text-[10px] uppercase tracking-wide px-1 border border-[var(--border)] ${
        level === "solid" ? "text-[var(--ink)]" : "text-[var(--ink-muted)]"
      }`}
    >
      {c.label}
    </span>
  );
}

function IndexBar({ metric, value }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0" title={`${METRIC_LONG[metric]}: ${formatIndex(value)} typical`}>
      <div className="an-track relative h-1.5 w-16 shrink-0">
        <div className="an-mid absolute top-[-2px] bottom-[-2px] left-1/2 w-px" />
        <div className={`an-bar-${metric} h-full`} style={{ width: `${indexBarWidth(value)}%` }} />
      </div>
      <span className="text-[11px] font-mono-display text-[var(--ink-muted)] w-9">{formatIndex(value)}</span>
    </div>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div className={`${CARD} p-4`}>
      <div className="overline text-[var(--ink-muted)] mb-1">{label}</div>
      <div className="font-serif-display text-3xl tracking-tight">{value}</div>
      {hint ? <div className="mt-1 text-xs text-[var(--ink-muted)]">{hint}</div> : null}
    </div>
  );
}

function ArticleRow({ a }) {
  const eng = engagementOf(a);
  return (
    <li className="py-2.5 border-b border-[var(--border)] last:border-b-0">
      <div className="flex items-start gap-3">
        <ScoreBadge score={a.score} tier={a.tier} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5">
            <Link
              to={`/article/${a.id}`}
              className="text-sm leading-snug hover:text-[var(--accent)]"
              data-testid={`analytics-article-${a.id}`}
            >
              {a.headline || "(untitled)"}
            </Link>
            {a.url ? (
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                aria-label="Open on website"
                className="shrink-0 mt-0.5 text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[var(--ink-muted)] font-mono-display">
            <span>{shortDate(a.local_date)}</span>
            {a.mature ? (
              <>
                <span title={METRIC_LONG.web}>Web {formatCount(a.web_d7)} · {formatIndex(a.idx?.web)}</span>
                <span title={METRIC_LONG.fb}>FB {formatCount(a.fb_views)} · {formatIndex(a.idx?.fb)}</span>
                <span title={METRIC_LONG.eng}>Eng {formatCount(eng)} · {formatIndex(a.idx?.eng)}</span>
              </>
            ) : (
              <span>
                So far: web {formatCount(a.web_so_far)} · FB {formatCount(a.fb_views_so_far)} · eng{" "}
                {formatCount(a.eng_so_far)}
              </span>
            )}
            {(a.category || []).filter((c) => !c.startsWith("(")).slice(0, 2).map((c) => (
              <span key={c}>{c}</span>
            ))}
            {(a.topic || []).filter((t) => !t.startsWith("(")).map((t) => (
              <span key={t} className="text-[var(--ink)]">#{t}</span>
            ))}
          </div>
        </div>
      </div>
    </li>
  );
}

function ArticleList({ articles, empty }) {
  if (!articles.length) {
    return <p className="text-sm text-[var(--ink-muted)] py-3">{empty}</p>;
  }
  return <ul>{articles.map((a) => <ArticleRow key={a.id} a={a} />)}</ul>;
}

function Section({ title, icon: Icon, children, right }) {
  return (
    <section className={`${CARD} p-4 sm:p-5 mb-6`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="flex items-center gap-2 font-serif-display text-xl tracking-tight">
          {Icon ? <Icon className="w-4 h-4 text-[var(--accent)]" /> : null}
          {title}
        </h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function InsightList({ title, items, Icon }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 overline text-[var(--ink-muted)] mb-2">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm leading-snug">
            <div>{it.point}</div>
            {it.evidence ? (
              <div className="text-xs text-[var(--ink-muted)] mt-0.5 font-mono-display">{it.evidence}</div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DigestCard({ digest, tz, onGenerate, generating, canGenerate }) {
  return (
    <Section
      title="What's working"
      icon={Sparkles}
      right={
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating || !canGenerate}
          className={BTN}
          data-testid="analytics-generate-insights"
          title={canGenerate ? "" : "Needs at least 5 scored stories in this range"}
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {digest ? "Write new insights" : "Write insights"}
        </button>
      }
    >
      {!digest ? (
        <p className="text-sm text-[var(--ink-muted)]">
          No insights yet. Newsdesk writes a fresh memo every Monday once there are enough scored
          stories, or you can write one now for the selected range.
        </p>
      ) : (
        <>
          {digest.headline ? (
            <p className="font-serif-display text-lg leading-snug mb-4">{digest.headline}</p>
          ) : null}
          <div className="grid gap-5 md:grid-cols-3">
            <InsightList title="Post more" items={digest.do_more} Icon={TrendingUp} />
            <InsightList title="Post less" items={digest.do_less} Icon={TrendingDown} />
            <InsightList title="Try next" items={digest.try_next} Icon={Lightbulb} />
          </div>
          {digest.caveats?.length ? (
            <p className="mt-4 text-xs text-[var(--ink-muted)]">Caveats: {digest.caveats.join(" ")}</p>
          ) : null}
          <p className="mt-3 text-[11px] font-mono-display text-[var(--ink-muted)]">
            Written {formatInTz(digest.created_at, tz, "compact")} · last {digest.range_days} days ·{" "}
            {digest.scored} scored stories · {digest.trigger === "weekly" ? "weekly memo" : "on request"}
          </p>
        </>
      )}
    </Section>
  );
}

function BreakdownTable({ groups, byId, openValue, setOpenValue }) {
  if (!groups?.length) {
    return (
      <p className="text-sm text-[var(--ink-muted)] py-2">
        Not enough scored stories to compare yet (groups need at least 3).
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--ink-muted)] border-b border-[var(--border)]">
            <th className="py-2 pr-3 font-medium">Group</th>
            <th className="py-2 pr-3 font-medium">Stories</th>
            <th className="py-2 pr-3 font-medium">Score</th>
            {METRIC_KEYS.map((k) => (
              <th key={k} className="py-2 pr-3 font-medium hidden md:table-cell" title={METRIC_LONG[k]}>
                {METRIC_SHORT[k]}
              </th>
            ))}
            <th className="py-2 pr-3 font-medium" title="Share of stories at or above typical on all three">
              All three
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const open = openValue === g.value;
            return (
              <FragmentRow
                key={g.value}
                g={g}
                open={open}
                onToggle={() => setOpenValue(open ? null : g.value)}
                articles={open ? groupArticles(g, byId) : []}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ g, open, onToggle, articles }) {
  return (
    <>
      <tr
        className="border-b border-[var(--border)] hover:bg-[var(--secondary)] cursor-pointer"
        onClick={onToggle}
        data-testid={`analytics-group-${g.value}`}
      >
        <td className="py-2 pr-3">
          <div className="flex items-center gap-1.5">
            {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
            <span className={g.value.startsWith("(") ? "text-[var(--ink-muted)] italic" : ""}>{g.value}</span>
          </div>
        </td>
        <td className="py-2 pr-3 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <span className="font-mono-display">{g.n}</span>
            <span className="text-[11px] text-[var(--ink-muted)]">({formatPct(g.share)})</span>
            <ConfidenceTag level={g.confidence} />
          </div>
        </td>
        <td className="py-2 pr-3"><ScoreBadge score={g.score} /></td>
        {METRIC_KEYS.map((k) => (
          <td key={k} className="py-2 pr-3 hidden md:table-cell"><IndexBar metric={k} value={g[k]} /></td>
        ))}
        <td className="py-2 pr-3 font-mono-display">{formatPct(g.all_three_rate)}</td>
      </tr>
      {open ? (
        <tr className="border-b border-[var(--border)]">
          <td colSpan={7} className="px-2 sm:px-6 pb-2 bg-[var(--paper)]">
            <ArticleList articles={articles} empty="No stories." />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Heatmap({ cells }) {
  const idx = heatIndex(cells);
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-separate" style={{ borderSpacing: 3 }}>
        <thead>
          <tr>
            <th />
            {HEAT_WEEKDAYS.map((d) => (
              <th key={d} className="px-1 font-medium text-[var(--ink-muted)]">{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HEAT_DAYPARTS.map((dp) => (
            <tr key={dp}>
              <th className="pr-2 text-left font-medium text-[var(--ink-muted)] whitespace-nowrap">{dp}</th>
              {HEAT_WEEKDAYS.map((d) => {
                const c = idx[`${d}|${dp}`];
                const tier = c && c.n >= 3 ? tierForScore(c.score) : null;
                return (
                  <td
                    key={d}
                    title={c ? `${d} ${dp}: ${formatIndex(c.score)} typical, ${c.n} stories` : "No stories"}
                    className={`w-14 h-10 text-center align-middle ${tier ? tierMeta(tier).cls : "an-tier-empty"}`}
                  >
                    {c ? (
                      <div className="leading-tight">
                        <div className="font-mono-display">{c.n >= 3 ? formatIndex(c.score) : "·"}</div>
                        <div className="text-[10px] opacity-80">{c.n}</div>
                      </div>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[11px] text-[var(--ink-muted)]">
        Median score of stories published in each slot (website go-live time). Slots with fewer than 3
        stories show a dot.
      </p>
    </div>
  );
}

function Trend({ weeks }) {
  if (!weeks?.length) return <p className="text-sm text-[var(--ink-muted)]">No stories in this range.</p>;
  const maxScore = Math.max(2, ...weeks.map((w) => w.score || 0));
  return (
    <div>
      <div className="flex items-end gap-1 h-36 overflow-x-auto pb-1">
        {weeks.map((w) => {
          const h = w.score ? Math.max(4, Math.round((w.score / maxScore) * 100)) : 0;
          return (
            <div key={w.week} className="flex flex-col items-center justify-end h-full min-w-[26px] flex-1">
              <div className="text-[10px] font-mono-display text-[var(--ink-muted)] mb-0.5">
                {w.score ? formatIndex(w.score) : ""}
              </div>
              <div
                className={`w-full ${w.score ? tierMeta(tierForScore(w.score)).cls : "an-tier-empty"}`}
                style={{ height: `${h}%` }}
                title={`Week of ${shortDate(w.week)}: ${w.posted} posted, ${w.scored} scored, median ${formatIndex(w.score)}`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 overflow-x-auto">
        {weeks.map((w) => (
          <div key={w.week} className="min-w-[26px] flex-1 text-center text-[10px] text-[var(--ink-muted)] leading-tight">
            <div>{shortDate(w.week)}</div>
            <div className="font-mono-display text-[var(--ink)]">{w.posted}</div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-[var(--ink-muted)]">
        Bars: median score of each week's stories. Numbers underneath: stories posted that week.
      </p>
    </div>
  );
}

export default function AnalyticsPage() {
  const qc = useQueryClient();
  const [days, setDays] = useState(90);
  const [dimension, setDimension] = useState("category");
  const [openValue, setOpenValue] = useState(null);
  const [sortKey, setSortKey] = useState("score");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);

  const overview = useQuery({
    queryKey: ["analytics-overview", days],
    queryFn: () => api.getAnalyticsOverview(days),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
  const status = useQuery({
    queryKey: ["analytics-status"],
    queryFn: api.getAnalyticsStatus,
    refetchInterval: (q) => (q.state.data?.sync?.status === "running" ? 5000 : false),
  });
  const digestQ = useQuery({
    queryKey: ["analytics-digest"],
    queryFn: api.getAnalyticsDigest,
    staleTime: 5 * 60 * 1000,
  });

  const syncMut = useMutation({
    mutationFn: api.runAnalyticsSync,
    onSuccess: () => {
      toast.success("Sync started — numbers will fill in over the next few minutes");
      qc.invalidateQueries({ queryKey: ["analytics-status"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start the sync"),
  });
  const digestMut = useMutation({
    mutationFn: () => api.generateAnalyticsDigest(days),
    onSuccess: (res) => {
      qc.setQueryData(["analytics-digest"], res);
      toast.success("Insights written");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not write insights"),
  });

  const data = overview.data;
  const articles = data?.articles || [];
  const byId = useMemo(() => Object.fromEntries(articles.map((a) => [a.id, a])), [articles]);
  const groups = data?.breakdowns?.[dimension] || [];
  const top = (data?.top_ids || []).map((id) => byId[id]).filter(Boolean);
  const bottom = (data?.bottom_ids || []).map((id) => byId[id]).filter(Boolean);
  const evergreen = (data?.evergreen || []).map((e) => byId[e.id]).filter(Boolean);
  const listed = useMemo(
    () => sortArticles(filterArticles(articles, { query, status: statusFilter }), sortKey),
    [articles, query, statusFilter, sortKey],
  );
  const k = data?.kpis;
  const sync = status.data?.sync;
  const running = sync?.status === "running";
  const dimLabel = (data?.dimensions || []).find((d) => d.key === dimension)?.label || "";

  // When a sync finishes, reload the numbers it produced.
  const prevSyncStatus = useRef(sync?.status);
  useEffect(() => {
    if (prevSyncStatus.current === "running" && sync?.status && sync.status !== "running") {
      qc.invalidateQueries({ queryKey: ["analytics-overview"] });
    }
    prevSyncStatus.current = sync?.status;
  }, [sync?.status, qc]);

  const pickDimension = (key) => {
    setDimension(key);
    setOpenValue(null);
  };

  const refreshAfterSync = () => {
    qc.invalidateQueries({ queryKey: ["analytics-overview"] });
    qc.invalidateQueries({ queryKey: ["analytics-status"] });
  };

  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-10 py-8 pb-48 md:pb-8">
        <div className="mb-6">
          <div className="overline mb-2">Newsroom · Analytics</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">Analytics</h1>
          <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-3xl">
            Which kinds of stories work. Each story is compared with your typical story on website views
            (first 7 days), Facebook views and social engagement — <b>1.0×</b> is typical, <b>2.0×</b> is
            double. A story that's at or above typical on all three is one that works. Stories are scored
            once they're 7 days old.
          </p>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3">
          <div role="group" aria-label="Date range" className="inline-flex">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={days === r}
                data-testid={`analytics-range-${r}`}
                onClick={() => { setDays(r); setOpenValue(null); }}
                className={segBtn(days === r)}
              >
                {r === 365 ? "1 year" : `${r} days`}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => syncMut.mutate()}
            disabled={running || syncMut.isPending}
            className={BTN}
            data-testid="analytics-sync-now"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
            {running ? "Syncing…" : "Sync now"}
          </button>
          {sync?.status === "ok" && sync.finished_at ? (
            <button type="button" onClick={refreshAfterSync} className="text-xs underline text-[var(--ink-muted)]">
              Reload numbers
            </button>
          ) : null}
          {overview.isFetching ? <Loader2 className="w-4 h-4 animate-spin text-[var(--ink-muted)]" /> : null}
        </div>

        <div className="mb-6 text-[11px] font-mono-display text-[var(--ink-muted)] space-y-1">
          <div>{coverageLine(status.data?.coverage)}</div>
          <div>
            {sync?.finished_at
              ? `Last sync ${formatInTz(sync.finished_at, tz, "compact")} (${sync.trigger || "?"})`
              : "Never synced"}
            {" · "}Syncs automatically every night at 3:00.
            {sync?.status === "error" ? <span className="an-text-weak"> Last sync failed: {sync.error}</span> : null}
          </div>
          {status.data && !status.data.web_configured ? (
            <div className="flex items-center gap-1.5 an-text-below">
              <AlertTriangle className="w-3.5 h-3.5" />
              Website views need WordPress.com connected (Settings → WordPress.com). Scores use Facebook only until then.
            </div>
          ) : null}
        </div>

        {overview.isError ? (
          <div className={`${CARD} p-4 mb-6 text-sm an-text-weak`}>
            Could not load analytics: {overview.error?.response?.data?.detail || overview.error?.message}
          </div>
        ) : null}

        {overview.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : data?.kpis ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Kpi
                label="Stories posted"
                value={k.articles}
                hint={`${k.scored} scored · ${k.maturing} under 7 days · ${k.syncing} waiting for numbers`}
              />
              <Kpi
                label="Worked on all three"
                value={formatPct(k.all_three_rate)}
                hint={`${k.all_three} of ${k.scored} scored stories`}
              />
              <Kpi
                label="Typical story"
                value={formatCount(k.median_web_d7)}
                hint={`web views in 7 days · ${formatCount(k.median_fb_views)} FB views · ${formatCount(k.median_engagement)} engagements`}
              />
              <Kpi
                label="Shared to social"
                value={`${k.on_facebook}`}
                hint={`on Facebook · ${k.on_instagram} on Instagram · ${k.tagged} of ${k.articles} tagged`}
              />
            </div>

            <DigestCard
              digest={digestQ.data?.digest}
              tz={tz}
              onGenerate={() => digestMut.mutate()}
              generating={digestMut.isPending}
              canGenerate={k.scored >= 5}
            />

            {data.underposted?.length ? (
              <Section title="Do more of these" icon={TrendingUp}>
                <p className="text-sm text-[var(--ink-muted)] mb-3">
                  Score well but make up less of what you post than a typical group.
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.underposted.map((u) => (
                    <button
                      key={`${u.dimension}|${u.value}`}
                      type="button"
                      onClick={() => { pickDimension(u.dimension); setOpenValue(u.value); }}
                      className={`${BTN} h-auto py-1.5`}
                    >
                      <span className="text-[var(--ink-muted)] text-xs">{u.dimension_label}:</span>
                      <span>{u.value}</span>
                      <ScoreBadge score={u.score} />
                      <span className="text-[11px] text-[var(--ink-muted)]">{u.n} stories · {formatPct(u.share)}</span>
                    </button>
                  ))}
                </div>
              </Section>
            ) : null}

            <Section
              title="Compare by"
              icon={BarChart3}
              right={
                <select
                  value={dimension}
                  onChange={(e) => pickDimension(e.target.value)}
                  className="h-8 px-2 text-sm border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)]"
                  data-testid="analytics-dimension"
                  aria-label="Compare by"
                >
                  {(data.dimensions || []).map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
              }
            >
              <p className="text-xs text-[var(--ink-muted)] mb-2">
                {dimLabel}: median score per group, best first. Click a group to see its stories.
                {dimension === "topic" || dimension === "place" || dimension === "format" || dimension === "audience"
                  ? " Tags are added automatically by AI."
                  : ""}
              </p>
              <BreakdownTable groups={groups} byId={byId} openValue={openValue} setOpenValue={setOpenValue} />
            </Section>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Best time to publish" icon={BarChart3}>
                <Heatmap cells={data.heatmap} />
              </Section>
              <Section title="Week by week" icon={TrendingUp}>
                <Trend weeks={data.trend} />
              </Section>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Top stories" icon={TrendingUp}>
                <ArticleList articles={top} empty="No scored stories yet." />
              </Section>
              <Section title="Lowest scoring" icon={TrendingDown}>
                <ArticleList articles={bottom} empty="Needs more than 10 scored stories." />
              </Section>
            </div>

            {evergreen.length ? (
              <Section title="Evergreen — still getting read" icon={Leaf}>
                <p className="text-sm text-[var(--ink-muted)] mb-2">
                  At least a third of their first-month website views came after the first week. Good
                  candidates to reshare, add to a newsletter or follow up.
                </p>
                <ArticleList articles={evergreen} empty="" />
              </Section>
            ) : null}

            <Section
              title="All stories"
              right={
                <button type="button" className={BTN} onClick={() => setShowAll((v) => !v)} data-testid="analytics-toggle-all">
                  {showAll ? "Hide" : `Show ${articles.length}`}
                </button>
              }
            >
              {showAll ? (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search headlines"
                      className="h-8 px-2 text-sm border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)] w-56"
                    />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-8 px-2 text-sm border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)]"
                      aria-label="Status"
                    >
                      <option value="all">All</option>
                      <option value="scored">Scored</option>
                      <option value="pending">Not scored yet</option>
                    </select>
                    <div className="inline-flex">
                      {Object.entries(SORTS).map(([key, s]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSortKey(key)}
                          className={segBtn(sortKey === key)}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ArticleList articles={listed} empty="No stories match." />
                </>
              ) : (
                <p className="text-sm text-[var(--ink-muted)]">
                  Every story in the range with its numbers, searchable and sortable.
                </p>
              )}
            </Section>
          </>
        ) : null}
      </div>
    </div>
  );
}
